import type { BoosterId } from '../defs/booster';
import type { Cell } from '../game/components/MapComponent';
import { type PowerBlockType } from '../game/defs/block';
import { config } from '../game/defs/config';
import type { BlockEntity } from '../game/entities/BlockEntity';
import type { GameScene } from '../game/GameScene';
import {
  blockGetCell,
  blockIsBinded,
  blockIsFrozen,
  blockIterateAll,
  blockNearestNeighbors,
} from '../game/util/blockTools';
import {
  isPowerBlock,
  matchFindGroups,
  type Match,
} from '../game/util/matchTools';

const LOLLIPOP_POWER_TYPE: PowerBlockType = 'cube';
const BOOSTER_CADENCE = 3;

/** Target selected for a power block or matching group tap. */
export type AutoPlayTapTarget = {
  kind: 'power' | 'group';
  blockId: string;
  cell: Cell;
  column: number;
  row: number;
  groupSize?: number;
};

/** Target selected for a free booster activation. */
export type AutoPlayBoosterTarget = {
  kind: 'booster';
  boosterId: BoosterId;
  blockId: BoosterId;
  column: number;
  row: number;
  obstacleCount: number;
};

/** Any board action that the autoplay system can perform. */
export type AutoPlayTarget = AutoPlayTapTarget | AutoPlayBoosterTarget;

/** Explanation recorded for an autoplay decision. */
export type AutoPlayDecisionReason =
  | 'lollipop-combo'
  | 'lollipop-setup-cube'
  | 'lollipop-setup-power'
  | 'lollipop-helper-power'
  | 'lollipop-spawn'
  | 'lollipop-booster'
  | 'fallback';

/** Selected autoplay target and the reason it was chosen. */
export type AutoPlayDecision = {
  target: AutoPlayTarget;
  reason: AutoPlayDecisionReason;
  goalHits?: number;
};

/** Match group that can create a power block. */
type PowerSpawnCandidate = {
  powerType: PowerBlockType;
  block: BlockEntity;
  groupSize: number;
  centerDistance: number;
  target: AutoPlayTarget;
};

/** Options that influence the next autoplay decision. */
export type AutoPlayOptions = {
  actionCount?: number;
};

type LollipopBoosterTarget = {
  boosterId: Extract<BoosterId, 'bullet' | 'drill'>;
  column: number;
  row: number;
  obstacleCount: number;
  lineDistance: number;
  lineIndex: number;
};

type Position = {
  column: number;
  row: number;
};

/**
 * Select the next lollipop-focused autoplay action.
 *
 * @param game Active Match2 game.
 * @param options Current autoplay progress.
 * @returns Next action, or undefined when no valid action exists.
 */
export function getAutoPlayDecision(
  game: GameScene,
  options: AutoPlayOptions = {},
): AutoPlayDecision | undefined {
  const existingCombo = getExistingLollipopComboTarget(game);
  if (existingCombo) {
    return {
      target: existingCombo,
      reason: 'lollipop-combo',
    };
  }

  if (isLollipopBoosterCadenceAction(options.actionCount ?? 0)) {
    const boosterDecision = getAutoPlayBoosterDecision(game);
    if (boosterDecision) return boosterDecision;
  }

  const powerCandidates = getPowerSpawnCandidates(game);
  const lollipopCandidates = powerCandidates.filter(
    (candidate) => candidate.powerType === LOLLIPOP_POWER_TYPE,
  );

  const besideLollipop = getBestAdjacentLollipopTarget(
    game,
    lollipopCandidates,
    isLollipopBlock,
  );
  if (besideLollipop) {
    return {
      target: besideLollipop,
      reason: 'lollipop-setup-cube',
    };
  }

  const besidePower = getBestAdjacentLollipopTarget(
    game,
    lollipopCandidates,
    () => true,
  );
  if (besidePower) {
    return {
      target: besidePower,
      reason: 'lollipop-setup-power',
    };
  }

  const helperPower = getBestPowerBesideLollipopTarget(game, powerCandidates);
  if (helperPower) {
    return {
      target: helperPower,
      reason: 'lollipop-helper-power',
    };
  }

  const standaloneLollipop = getBestStandaloneLollipopTarget(
    game,
    lollipopCandidates,
  );
  if (standaloneLollipop) {
    return {
      target: standaloneLollipop,
      reason: 'lollipop-spawn',
    };
  }

  const fallbackTarget = getFallbackAutoPlayTarget(game);
  if (!fallbackTarget) return undefined;

  return {
    target: fallbackTarget,
    reason: 'fallback',
  };
}

/**
 * Find every match group that can create a power block.
 *
 * @param game Active Match2 game.
 * @returns Power-spawn candidates ordered from the board center outward.
 */
function getPowerSpawnCandidates(game: GameScene): PowerSpawnCandidate[] {
  const candidates: PowerSpawnCandidate[] = [];

  matchFindGroups(game).forEach((group) => {
    const powerType = getCreatedPowerType(group.matches.length);
    if (!powerType) return;

    for (const match of group.matches) {
      const target = getGroupTarget(game, match, group.matches.length);
      if (!target) continue;

      candidates.push({
        powerType,
        block: match.base,
        groupSize: group.matches.length,
        centerDistance: getCenterDistance(game, match.base),
        target,
      });
    }
  });

  return candidates.sort(comparePowerSpawnCandidates);
}

/**
 * Determine which power block a match group creates.
 *
 * @param matchCount Number of blocks in the match group.
 * @returns Created power type, or undefined when the group is too small.
 */
function getCreatedPowerType(matchCount: number): PowerBlockType | undefined {
  let powerType: PowerBlockType | undefined;
  let highCount = 0;

  for (const [type, count] of Object.entries(config.blocks.basic.powerMatch)) {
    if (count >= highCount && count <= matchCount) {
      powerType = type as PowerBlockType;
      highCount = count;
    }
  }

  return powerType;
}

/**
 * Measure a block's Manhattan distance from the board center.
 *
 * @param game Active Match2 game.
 * @param block Block to measure.
 * @returns Distance from the center.
 */
function getCenterDistance(game: GameScene, block: BlockEntity): number {
  const map = game.sessionEntity.c.map;
  const position = block.c.position.mapPosition;
  const centerX = (map.columns - 1) / 2;
  const centerY = (map.rows - 1) / 2;

  return Math.abs(position.x - centerX) + Math.abs(position.y - centerY);
}

/**
 * Find tappable power blocks ordered from the board center outward.
 *
 * @param game Active Match2 game.
 * @returns Tappable power blocks.
 */
function getPowerBlocksCenterOut(game: GameScene): BlockEntity[] {
  const blocks: BlockEntity[] = [];

  blockIterateAll(game, (base) => {
    if (canTapPowerBlock(game, base)) {
      blocks.push(base);
    }

    return false;
  });

  return blocks.sort(
    (a, b) => getCenterDistance(game, a) - getCenterDistance(game, b),
  );
}

/**
 * Find tappable power blocks adjacent to another block.
 *
 * @param game Active Match2 game.
 * @param block Block whose neighbors should be checked.
 * @returns Adjacent tappable power blocks.
 */
function getAdjacentPowerBlocks(
  game: GameScene,
  block: BlockEntity,
): BlockEntity[] {
  return (
    blockNearestNeighbors(game.sessionEntity.c.map, block, (neighbor) =>
      canTapPowerBlock(game, neighbor),
    )?.filter((neighbor): neighbor is BlockEntity => Boolean(neighbor)) ?? []
  );
}

/**
 * Find the nearest matching power block to a block.
 *
 * @param game Active Match2 game.
 * @param block Block to measure from.
 * @param predicate Optional power-block filter.
 * @returns Manhattan distance to the nearest matching power block.
 */
function getNearestPowerDistance(
  game: GameScene,
  block: BlockEntity,
  predicate: (powerBlock: BlockEntity) => boolean = () => true,
): number {
  let distance = Number.MAX_SAFE_INTEGER;
  const position = block.c.position.mapPosition;

  blockIterateAll(game, (base) => {
    if (!canTapPowerBlock(game, base) || !predicate(base)) return false;

    const powerPosition = base.c.position.mapPosition;
    distance = Math.min(
      distance,
      Math.abs(position.x - powerPosition.x) +
        Math.abs(position.y - powerPosition.y),
    );

    return false;
  });

  return distance;
}

/**
 * Check whether a power block can currently be tapped.
 *
 * @param game Active Match2 game.
 * @param block Power block to check.
 * @returns Whether the block is tappable.
 */
function canTapPowerBlock(game: GameScene, block: BlockEntity): boolean {
  if (!isPowerBlock(block)) return false;
  if (blockIsFrozen(game, block) || blockIsBinded(game, block)) return false;

  const cell = blockGetCell(game, block);
  if (!cell?.tile) return false;
  if (cell.overlay && !cell.overlay.entity.c.block.props.tappable) return false;

  return true;
}

/**
 * Build an autoplay target for a matching group block.
 *
 * @param game Active Match2 game.
 * @param match Match whose base block should be tapped.
 * @param groupSize Number of blocks in the group.
 * @returns Tappable group target, or undefined when blocked.
 */
function getGroupTarget(
  game: GameScene,
  match: Match,
  groupSize: number,
): AutoPlayTarget | undefined {
  const cell = blockGetCell(game, match.base);
  if (!cell?.tile) return undefined;
  if (cell.overlay && !cell.overlay.entity.c.block.props.tappable) {
    return undefined;
  }

  const position = match.base.c.position.mapPosition;
  return {
    kind: 'group',
    blockId: match.base.c.block.blockId,
    cell,
    column: position.x,
    row: position.y,
    groupSize,
  };
}

/**
 * Build an autoplay target for a power block.
 *
 * @param game Active Match2 game.
 * @param block Power block to tap.
 * @returns Tappable power target, or undefined when blocked.
 */
function getPowerTarget(
  game: GameScene,
  block: BlockEntity,
): AutoPlayTarget | undefined {
  if (!canTapPowerBlock(game, block)) return undefined;

  const cell = blockGetCell(game, block);
  if (!cell?.tile) return undefined;

  const position = block.c.position.mapPosition;
  return {
    kind: 'power',
    blockId: block.c.block.blockId,
    cell,
    column: position.x,
    row: position.y,
  };
}

/**
 * Compare power-spawn candidates by distance from the board center.
 *
 * @param a First candidate.
 * @param b Second candidate.
 * @returns Sort comparison value.
 */
function comparePowerSpawnCandidates(
  a: PowerSpawnCandidate,
  b: PowerSpawnCandidate,
): number {
  return a.centerDistance - b.centerDistance;
}

/**
 * Select a simple move when no lollipop-specific action is available.
 *
 * @param game Active Match2 game.
 * @returns A tappable power or largest matching group.
 */
function getFallbackAutoPlayTarget(
  game: GameScene,
): AutoPlayTarget | undefined {
  for (const block of getPowerBlocksCenterOut(game)) {
    const target = getPowerTarget(game, block);
    if (target) return target;
  }

  const groups = matchFindGroups(game).sort((a, b) => {
    return b.matches.length - a.matches.length;
  });

  for (const group of groups) {
    for (const match of group.matches) {
      const target = getGroupTarget(game, match, group.matches.length);
      if (target) return target;
    }
  }

  return undefined;
}

function getExistingLollipopComboTarget(
  game: GameScene,
): AutoPlayTarget | undefined {
  let best:
    | {
        rank: number;
        centerDistance: number;
        target: AutoPlayTarget;
      }
    | undefined;

  for (const block of getPowerBlocksCenterOut(game)) {
    for (const adjacent of getAdjacentPowerBlocks(game, block)) {
      if (!isLollipopBlock(block) && !isLollipopBlock(adjacent)) continue;

      const targetBlock = isLollipopBlock(block) ? block : adjacent;
      const target = getPowerTarget(game, targetBlock);
      if (!target) continue;

      const rank = isLollipopBlock(block) && isLollipopBlock(adjacent) ? 0 : 1;
      const centerDistance = getCenterDistance(game, targetBlock);

      if (
        best &&
        (best.rank < rank ||
          (best.rank === rank && best.centerDistance <= centerDistance))
      ) {
        continue;
      }

      best = { rank, centerDistance, target };
      if (rank === 0) return best.target;
    }
  }

  return best?.target;
}

function getBestAdjacentLollipopTarget(
  game: GameScene,
  candidates: PowerSpawnCandidate[],
  predicate: (powerBlock: BlockEntity) => boolean,
): AutoPlayTarget | undefined {
  let best:
    | {
        centerDistance: number;
        groupSize: number;
        target: AutoPlayTarget;
      }
    | undefined;

  for (const candidate of candidates) {
    const hasAdjacentPower = getAdjacentPowerBlocks(game, candidate.block).some(
      predicate,
    );
    if (!hasAdjacentPower) continue;

    if (
      best &&
      (best.centerDistance < candidate.centerDistance ||
        (best.centerDistance === candidate.centerDistance &&
          best.groupSize >= candidate.groupSize))
    ) {
      continue;
    }

    best = {
      centerDistance: candidate.centerDistance,
      groupSize: candidate.groupSize,
      target: candidate.target,
    };
  }

  return best?.target;
}

function getBestStandaloneLollipopTarget(
  game: GameScene,
  candidates: PowerSpawnCandidate[],
): AutoPlayTarget | undefined {
  let best:
    | {
        powerDistance: number;
        centerDistance: number;
        groupSize: number;
        target: AutoPlayTarget;
      }
    | undefined;

  for (const candidate of candidates) {
    const powerDistance = getNearestPowerDistance(game, candidate.block);

    if (
      best &&
      (best.powerDistance < powerDistance ||
        (best.powerDistance === powerDistance &&
          best.centerDistance < candidate.centerDistance) ||
        (best.powerDistance === powerDistance &&
          best.centerDistance === candidate.centerDistance &&
          best.groupSize >= candidate.groupSize))
    ) {
      continue;
    }

    best = {
      powerDistance,
      centerDistance: candidate.centerDistance,
      groupSize: candidate.groupSize,
      target: candidate.target,
    };
  }

  return best?.target;
}

function getBestPowerBesideLollipopTarget(
  game: GameScene,
  candidates: PowerSpawnCandidate[],
): AutoPlayTarget | undefined {
  let best:
    | {
        powerRank: number;
        centerDistance: number;
        groupSize: number;
        target: AutoPlayTarget;
      }
    | undefined;

  for (const candidate of candidates) {
    if (candidate.powerType === LOLLIPOP_POWER_TYPE) continue;

    const hasAdjacentLollipop = getAdjacentPowerBlocks(
      game,
      candidate.block,
    ).some(isLollipopBlock);
    if (!hasAdjacentLollipop) continue;

    const powerRank = getHelperPowerRank(candidate.powerType);
    if (
      best &&
      (best.powerRank < powerRank ||
        (best.powerRank === powerRank &&
          best.centerDistance < candidate.centerDistance) ||
        (best.powerRank === powerRank &&
          best.centerDistance === candidate.centerDistance &&
          best.groupSize >= candidate.groupSize))
    ) {
      continue;
    }

    best = {
      powerRank,
      centerDistance: candidate.centerDistance,
      groupSize: candidate.groupSize,
      target: candidate.target,
    };
  }

  return best?.target;
}

function isLollipopBlock(block: BlockEntity): boolean {
  return block.c.block.props.type === LOLLIPOP_POWER_TYPE;
}

function getHelperPowerRank(powerType: PowerBlockType): number {
  if (powerType === 'rocket') return 0;
  if (powerType === 'bomb') return 1;
  return 2;
}

export function isLollipopBoosterCadenceAction(actionCount: number): boolean {
  return (actionCount + 1) % BOOSTER_CADENCE === 0;
}

function getAutoPlayBoosterDecision(
  game: GameScene,
): AutoPlayDecision | undefined {
  const target = getBestObstacleBoosterTarget(game);
  if (!target) return undefined;

  return {
    target: {
      kind: 'booster',
      boosterId: target.boosterId,
      blockId: target.boosterId,
      column: target.column,
      row: target.row,
      obstacleCount: target.obstacleCount,
    },
    reason: 'lollipop-booster',
  };
}

export function getBestObstacleBoosterTarget(
  game: GameScene,
): LollipopBoosterTarget | undefined {
  const map = game.sessionEntity.c.map;
  const columnObstacles = Array.from({ length: map.columns }, () => ({
    entities: new Set<BlockEntity>(),
    positions: [] as Position[],
  }));
  const rowObstacles = Array.from({ length: map.rows }, () => ({
    entities: new Set<BlockEntity>(),
    positions: [] as Position[],
  }));

  for (let column = 0; column < map.columns; column++) {
    for (let row = 0; row < map.rows; row++) {
      const cell = map.grid[column][row];
      if (!cell?.enabled) continue;

      for (const entity of getObstacleEntities(cell)) {
        columnObstacles[column].entities.add(entity);
        columnObstacles[column].positions.push({ column, row });
        rowObstacles[row].entities.add(entity);
        rowObstacles[row].positions.push({ column, row });
      }
    }
  }

  const columnTarget = getBestColumnTarget(
    columnObstacles,
    map.columns,
    map.rows,
  );
  const rowTarget = getBestRowTarget(rowObstacles, map.columns, map.rows);

  if (!columnTarget) return rowTarget;
  if (!rowTarget) return columnTarget;

  return compareLineTargets(columnTarget, rowTarget) <= 0
    ? columnTarget
    : rowTarget;
}

function getBestColumnTarget(
  obstacles: { entities: Set<BlockEntity>; positions: Position[] }[],
  columns: number,
  rows: number,
): LollipopBoosterTarget | undefined {
  const center = (columns - 1) / 2;
  let best: LollipopBoosterTarget | undefined;

  obstacles.forEach((line, column) => {
    const obstacleCount = line.entities.size;
    if (obstacleCount === 0) return;

    const position = getBestPosition(line.positions, columns, rows);
    const target: LollipopBoosterTarget = {
      boosterId: 'drill',
      column,
      row: position.row,
      obstacleCount,
      lineDistance: Math.abs(column - center),
      lineIndex: column,
    };

    if (!best || compareLineTargets(target, best) < 0) {
      best = target;
    }
  });

  return best;
}

function getBestRowTarget(
  obstacles: { entities: Set<BlockEntity>; positions: Position[] }[],
  columns: number,
  rows: number,
): LollipopBoosterTarget | undefined {
  const center = (rows - 1) / 2;
  let best: LollipopBoosterTarget | undefined;

  obstacles.forEach((line, row) => {
    const obstacleCount = line.entities.size;
    if (obstacleCount === 0) return;

    const position = getBestPosition(line.positions, columns, rows);
    const target: LollipopBoosterTarget = {
      boosterId: 'bullet',
      column: position.column,
      row,
      obstacleCount,
      lineDistance: Math.abs(row - center),
      lineIndex: row,
    };

    if (!best || compareLineTargets(target, best) < 0) {
      best = target;
    }
  });

  return best;
}

function getBestPosition(
  positions: Position[],
  columns: number,
  rows: number,
): Position {
  const centerColumn = (columns - 1) / 2;
  const centerRow = (rows - 1) / 2;

  return positions.reduce((best, position) => {
    const bestDistance =
      Math.abs(best.column - centerColumn) + Math.abs(best.row - centerRow);
    const distance =
      Math.abs(position.column - centerColumn) +
      Math.abs(position.row - centerRow);

    if (distance < bestDistance) return position;
    if (distance > bestDistance) return best;
    if (position.row < best.row) return position;
    if (position.row > best.row) return best;
    return position.column < best.column ? position : best;
  });
}

function compareLineTargets(
  a: LollipopBoosterTarget,
  b: LollipopBoosterTarget,
): number {
  if (a.obstacleCount !== b.obstacleCount) {
    return b.obstacleCount - a.obstacleCount;
  }

  if (a.lineDistance !== b.lineDistance) {
    return a.lineDistance - b.lineDistance;
  }

  if (a.boosterId !== b.boosterId) {
    return a.boosterId === 'drill' ? -1 : 1;
  }

  return a.lineIndex - b.lineIndex;
}

function getObstacleEntities(cell: Cell): BlockEntity[] {
  const entities: BlockEntity[] = [];

  for (const layer of [cell.base, cell.overlay]) {
    const entity = layer?.entity;
    if (!entity) continue;
    if (entity.c.block.props.type === 'basic') continue;

    entities.push(entity);
  }

  return entities;
}
